import { useEffect } from 'react';
import { equals, isEmpty, omit, prop } from 'ramda';
import { useSetPlayerTags } from 'server';

import { useTranslations } from '@core/js/hooks/i18n';
import { htcmdService } from '@core/js/services/htcmd.service';
import { useCookieSettingsSelector } from '@core/js/store/hooks';

import { useDynamicTags } from '@interlayer/js/custom/hooks/useDynamicTags';
import { useQueryParam } from '@interlayer/js/custom/hooks/useQueryParam';
import { QUERY_PARAMS } from '@interlayer/js/custom/hooks/useQueryParam/constants';
import { useCustomUserSelector } from '@interlayer/js/custom/stores/user';
import { DYNAMIC_TAG_NAMES } from '@interlayer/js/custom/utils/constants';

export const useSetCookieGroupsToPlayerTag = () => {
    const setPlayerTags = useSetPlayerTags();
    const groupsData = useCookieSettingsSelector(prop('groupsData'));
    const allPlayerTags = useCustomUserSelector(prop('allPlayerTags'));
    const t = useTranslations('playtech.mobile.system.cookieLayer.');
    const cookieLayerMarketingQuery = useQueryParam(QUERY_PARAMS.CookieLayerMarketing, false);

    const [
        dTagCookieSiteConsent,
        dTagCookieNativeConsent
    ] = useDynamicTags([
        DYNAMIC_TAG_NAMES.COOKIE_SITE_CONSENT_TAG_NAME,
        DYNAMIC_TAG_NAMES.COOKIE_NATIVE_CONSENT_TAG_NAME
    ]);

    useEffect(() => {
        const setSiteConsentTag = () => {
            if (!allPlayerTags || isEmpty(groupsData) || !dTagCookieSiteConsent) {
                return;
            }

            try {
                const cookieSiteConsent = omit(['date'], JSON.parse(allPlayerTags[dTagCookieSiteConsent]?.value || '{}'));

                if (!equals(cookieSiteConsent, groupsData)) {
                    setPlayerTags({
                        tags: [{
                            type: 'text',
                            name: dTagCookieSiteConsent,
                            value: JSON.stringify({
                                ...groupsData,
                                date: new Date()
                            })
                        }]
                    });
                }
            } catch (error) {
                console.error(`Player tag ${dTagCookieSiteConsent} is not set`, error);
            }
        };

        setSiteConsentTag();
    }, [allPlayerTags, dTagCookieSiteConsent, groupsData, setPlayerTags]);

    const marketingTranslation = t('marketing');

    useEffect(() => {
        const setNativeConsentTag = () => {
            if (
                !allPlayerTags
                || !dTagCookieNativeConsent
                || !(htcmdService.isEnabled || htcmdService.isWrapperEnabled)
            ) {
                return;
            }

            const cookieSiteConsent = allPlayerTags[dTagCookieNativeConsent];
            const marketingTag = `${marketingTranslation}=${cookieLayerMarketingQuery}`;

            if (cookieLayerMarketingQuery && cookieSiteConsent?.value !== marketingTag) {
                setPlayerTags({
                    tags: [{
                        type: 'text',
                        name: dTagCookieNativeConsent,
                        value: marketingTag
                    }]
                });
            }
        };

        setNativeConsentTag();
    }, [allPlayerTags, cookieLayerMarketingQuery, dTagCookieNativeConsent, marketingTranslation, setPlayerTags]);
};
